import { Component, OnInit, HostListener, ViewChild } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router, ActivatedRoute } from "@angular/router";
import { FormGroup, FormArray, AbstractControl } from "@angular/forms";

import { MainConfig, FormToolsService, WindowRefService } from "@atenea-cms";
import { Category, CategoryService } from "../../categories/shared";
import { Serie, SerieService } from "../../series/shared";
import { Award, AwardService } from "../../awards/shared";
import { Reseller, ResellerService } from "../../resellers/shared";
import { DownloadType, DownloadTypeService } from "../../download-types/shared";
import { Product, ProductService, ProductFormService } from "../shared";
import { CrudBoxComponent } from "@atenea-cms";
import { NotifyService } from "@atenea-cms";
import { LocaleDataService } from "@atenea-cms";
import { isArray } from "util";

@Component({
  selector: "product-detail",
  templateUrl: "./product-detail.component.html",
  styleUrls: ["./product-detail.component.scss"],
})
export class ProductDetailComponent implements OnInit {
  @ViewChild(CrudBoxComponent) crudBox: CrudBoxComponent;

  loading: boolean = false;
  form: FormGroup;
  categories: Array<Category> = [];
  series: Array<Serie> = [];
  awards: Array<Award> = [];
  resellers: Array<Reseller> = [];
  downloadTypes: Array<DownloadType> = [];
  parentProducts: Array<Product> = [];
  modelTypes: Array<any> = [];
  previewWindow: any = null;
  originalProduct: Product = new Product();

  constructor(
    private titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    public ft: FormToolsService,
    public mainConfig: MainConfig,
    private categoryService: CategoryService,
    private serieService: SerieService,
    private awardService: AwardService,
    private resellerService: ResellerService,
    private downloadTypeService: DownloadTypeService,
    private productService: ProductService,
    private formService: ProductFormService,
    private windowRef: WindowRefService,
    private notify: NotifyService,
    private localeDataService: LocaleDataService
  ) {}

  ngOnInit() {
    this.titleService.setTitle("Productos - " + this.mainConfig.cmsName);
    this.loading = true;

    this.route.params.subscribe((params) => {
      this.categoryService.getAll().subscribe((categories) => {
        this.categories = categories;

        this.serieService.getAll().subscribe((series) => {
          this.series = series;

          this.awardService.getAll().subscribe((awards) => {
            this.awards = awards;

            this.resellerService.getAll().subscribe((resellers) => {
              this.resellers = resellers.filter(
                (item: Reseller) => item.type === 2 // solo tiendas
              );

              this.downloadTypeService.getAll().subscribe((downloadTypes) => {
                this.downloadTypes = downloadTypes;

                this.productService.getParents().subscribe((parents) => {
                  this.parentProducts = params["id"]
                    ? parents.filter((item) => item._id !== params["id"])
                    : parents;

                  if (params["id"]) {
                    this.productService
                      .getById(params["id"])
                      .subscribe((item) => {
                        item = this.transformFormValueBeforeLoad(item);

                        this.form = this.formService.getForm(item);
                        // this.checkChanges();
                        this.loading = false;
                      });
                  } else {
                    this.form = this.formService.getForm();
                    // this.checkChanges();
                    this.loading = false;
                  }
                });
              });
            });
          });
        });
      });
    });
  }

  refresh(id) {
    this.router.navigate(["/products/" + id]);
  }

  goToBack() {
    this.router.navigate(["/products"]);
  }

  transformFormValueBeforeLoad = (product: Product) => {
    this.originalProduct = JSON.parse(JSON.stringify(product));

    product.awards =
      product.awards && isArray(product.awards)
        ? product.awards.filter(
            (item) =>
              item.locale === this.localeDataService.getCurrentLocale().code
          )
        : [];

    if (this.form) {
      const awardsForm = <FormArray>this.form.get("awards");

      awardsForm.controls = awardsForm.controls.filter((item) => {
        return (
          item.value.locale === this.localeDataService.getCurrentLocale().code
        );
      });
    }

    return product;
  };

  transformFormValueBeforeSave = (form: FormGroup) => {
    if (
      form.get("active").value === true &&
      (form.get("published").value === null ||
        form.get("published").value === "")
    ) {
      form.get("published").patchValue(new Date());
    }

    const modelType: AbstractControl = form.get("modelType");

    if (modelType.value === "none") {
      form.get("parent").patchValue(null);
    }

    const slides = <FormArray>form.get("slides");

    slides.controls.forEach((slide: FormGroup) => {
      if (slide.get("backgroundColorAlias").value !== "custom") {
        slide.get("backgroundColor").patchValue(null);
      }
    });

    // Reasignamos los premios que de otros idiomas para que no se pierdan al guardar
    const awardsForm = <FormArray>this.form.get("awards");
    this.originalProduct.awards
      .filter(
        (award) =>
          award.locale !== this.localeDataService.getCurrentLocale().code
      )
      .forEach((award) => {
        awardsForm.push(this.formService.getAwardForm(award));
      });

    return form.value;
  };

  getCrudTitle() {
    const name = this.form.get("name").value;

    return name.trim() ? name : "Producto";
  }

  preview() {
    if (this.isPreviewAvailable) {
      const categoryId = this.form.get("category").value;

      if (!categoryId) {
        this.notify.danger(
          "Debes establecer una categoría para poder previsualizar un producto."
        );
        return false;
      }

      if (this.form.valid) {
        const $suscribe = this.crudBox.onSave.subscribe((savedItem) => {
          const category = this.categories.find(
            (item) => item._id === categoryId
          );
          const locale = this.localeDataService.getCurrentLocale();
          const localeSlug =
            this.localeDataService.getDefaultLocale() !== locale
              ? `/${locale.code}`
              : "";
          const slug = this.form.get("slug").value;
          const url = `${this.mainConfig.webUrl}${localeSlug}/${category.slug}/${slug}`;

          this.windowRef.nativeWindow.open(url, "product_preview");
          $suscribe.unsubscribe();
        });
      }

      this.crudBox.save();
    }
  }

  get isPreviewAvailable(): boolean {
    return (
      (!this.crudBox || !this.crudBox.loading) &&
      !this.form.get("active").value &&
      this.form.get("preview").value
    );
  }

  @HostListener("document:keydown", ["$event"])
  detectShortcut(e) {
    if (e.ctrlKey && e.altKey) {
      if (String.fromCharCode(e.keyCode) === "P") {
        e.preventDefault();
        this.preview();
      }
    }
  }

  sendToStore() {
    if (
      !confirm(
        `¿Deseas enviar el producto ${this.form.get("name").value} a la tienda?`
      )
    ) {
      return;
    }

    this.productService.sendToStore(this.form.value).subscribe(
      (result) => {
        this.notify.success(
          "El producto se ha exportado a la tienda correctamente"
        );
        this.viewInStore();
      },
      (error) => this.notify.danger(error._body)
    );
  }

  viewInStore() {
    const product = this.form.value;
    window.open(
      `${this.mainConfig.store}/producto/${product.slug}`,
      `${product._id}_${this.localeDataService.getCurrentLocale()}`
    );
  }
}



// WEBPACK FOOTER //
// ./libs/atenea-cms-brand/src/products/product-detail/product-detail.component.ts